import { useModal, useToastr } from '@farmshare/ui-components';
import { Box, Button, Button as MuiButton, Stack } from '@mui/material';
import { Archive as ArchiveIcon } from '@mui/icons-material';
import { EnumCutsheetQuestionnaireQuestionsType } from 'lib/graphql';
import {
  AddableQuestionType,
  ChoiceGroupQuestionForm,
  ExtrasSelectionQuestionForm,
  FreeSelectionQuestionForm,
  GlobalSpecQuestionForm,
  InfoSlideQuestionForm,
  ItemPickerBody,
  QuestionnaireInfoForm,
  QuestionnaireSettingsForm,
  TrimPercentageQuestionForm,
  TrimRankingQuestionForm,
} from '../components';
import type {
  CutsheetCatalog,
  CutsheetQuestionnaire,
  Question,
} from '../types';

interface UseQuestionHandlersParams {
  capabilities: CutsheetCatalog;
  questionnaire: CutsheetQuestionnaire;
  /** The capability set ID for grouping S3 image uploads. */
  capabilityId: string;
  /**
   * Optional raw capability (with printSettings, vendor, species) used by the
   * questionnaire settings modal to render the PDF preview and diff against
   * the base print settings.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  capability?: any;
  addQuestionAtIndex: (question: any, index: number) => void;
  updateQuestion: (id: string, values: any) => void;
  removeQuestion: (id: string) => void;
  addItemsToQuestion: (questionId: string, itemIds: string[]) => void;
  removeItemFromQuestion: (questionId: string, itemId: string) => void;
  reorderItemsInQuestion: (questionId: string, newOrderIds: string[]) => void;
  updateQuestionnaireInfo: (values: any) => void;
  saveQuestionnaire: (
    overrides?: Partial<CutsheetQuestionnaire>,
  ) => Promise<any>;
  onArchiveForm?: () => void;
}

/**
 * Encapsulates all modal-driven handlers for adding, editing, and deleting
 * questionnaire questions. Also includes item-level handlers (add/remove items
 * on questions, add/remove cuts from ChoiceGroup options).
 * @param root0 - Hook parameters
 * @param root0.capabilities - Available catalog capabilities
 * @param root0.questionnaire - The current questionnaire being built
 * @param root0.capabilityId - The capability set ID for S3 image grouping
 * @param root0.addQuestionAtIndex - Adds a question at a specific index
 * @param root0.updateQuestion - Updates an existing question by ID
 * @param root0.removeQuestion - Removes a question by ID
 * @param root0.addItemsToQuestion - Adds items to a question
 * @param root0.removeItemFromQuestion - Removes an item from a question
 * @param root0.reorderItemsInQuestion - Reorders items within a question
 * @param root0.updateQuestionnaireInfo - Updates questionnaire metadata
 * @param root0.saveQuestionnaire
 * @param root0.onArchiveForm
 * @returns Collection of modal-driven question handlers
 */
export const useQuestionHandlers = ({
  capabilities,
  questionnaire,
  capabilityId,
  capability,
  addQuestionAtIndex,
  updateQuestion,
  removeQuestion,
  addItemsToQuestion,
  removeItemFromQuestion,
  reorderItemsInQuestion,
  updateQuestionnaireInfo,
  saveQuestionnaire,
  onArchiveForm,
}: UseQuestionHandlersParams) => {
  const { save, ask } = useModal();
  const { push } = useToastr();

  // ---------------------------------------------------------------------------
  // Item-level handlers
  // ---------------------------------------------------------------------------

  /**
   * For GlobalSpec questions, defaults the question's title/description/imageUrl
   * from the selected specification if those fields are currently empty.
   * @param questionId
   * @param specId
   */
  const applyGlobalSpecDefaults = (questionId: string, specId: string) => {
    const question = questionnaire.questions.find((q) => q.id === questionId);
    if (
      !question ||
      question.type !== EnumCutsheetQuestionnaireQuestionsType.GlobalSpec
    )
      return;
    const spec = capabilities.specifications.find((s) => s.id === specId);
    if (!spec) return;
    updateQuestion(questionId, {
      title: spec.title,
      description: spec.description ?? '',
      imageUrl: spec.imageUrl ?? '',
    });
  };

  /**
   * Adds a single item to a question (delegates to the batch handler).
   * @param questionId
   * @param itemId
   */
  const handleAddItemToQuestion = (questionId: string, itemId: string) => {
    applyGlobalSpecDefaults(questionId, itemId);
    addItemsToQuestion(questionId, [itemId]);
  };

  /**
   * Adds multiple items to a question at once (from the click-to-select modal).
   * @param questionId
   * @param itemIds
   */
  const handleAddItemsToQuestion = (questionId: string, itemIds: string[]) => {
    if (itemIds.length === 0) return;
    if (itemIds.length === 1) applyGlobalSpecDefaults(questionId, itemIds[0]);
    addItemsToQuestion(questionId, itemIds);
  };

  const handleRemoveItemFromQuestion = (questionId: string, itemId: string) => {
    removeItemFromQuestion(questionId, itemId);
  };

  const handleReorderItemsInQuestion = (
    questionId: string,
    newOrderIds: string[],
  ) => {
    reorderItemsInQuestion(questionId, newOrderIds);
  };

  // ---------------------------------------------------------------------------
  // ChoiceGroup option-level handlers
  // ---------------------------------------------------------------------------

  const handleAddCutToOption = (questionId: string, optionId: string) => {
    const question = questionnaire.questions.find((q) => q.id === questionId);
    if (
      !question ||
      question.type !== EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup
    )
      return;

    const choiceQuestion = question as import('../types').ChoiceGroupQuestion;
    const option = choiceQuestion.options.find((opt) => opt.id === optionId);
    if (!option) return;

    if (capabilities.cuts.length === 0) {
      push({
        title: 'No Cuts Available',
        body: 'Add cuts in the Capabilities section first.',
        bg: 'info',
      });
      return;
    }

    // Enrich with primal group label for sectioned display
    const cutItems = capabilities.cuts.map((cut) => ({
      ...cut,
      groupLabel: cut.groupId
        ? (capabilities.primals.find((p) => p.id === cut.groupId)?.title ??
          'Other')
        : 'Other',
    }));

    ask({
      type: 'save',
      title: 'Manage Cuts for Option',
      initialValues: { selectedIds: [] as string[] },
      size: 'xs',
      footer: ({ modalProps }) =>
        (
          <Button fullWidth variant="contained" onClick={modalProps.onClose}>
            Done
          </Button>
        ) as React.ReactNode,
      body: (
        <ItemPickerBody
          items={cutItems}
          selectedIds={option.cutIds}
          accentColor="primary.main"
          onAdd={(cutId) => {
            // Guard against duplicates (e.g. rapid double-click)
            if (option.cutIds.includes(cutId)) return;
            const updatedOptions = choiceQuestion.options.map((opt) =>
              opt.id === optionId
                ? { ...opt, cutIds: [...opt.cutIds, cutId] }
                : opt,
            );
            updateQuestion(questionId, { options: updatedOptions });
          }}
          onRemove={(cutId) => {
            const updatedOptions = choiceQuestion.options.map((opt) =>
              opt.id === optionId
                ? {
                    ...opt,
                    cutIds: opt.cutIds.filter((id: string) => id !== cutId),
                  }
                : opt,
            );
            updateQuestion(questionId, { options: updatedOptions });
          }}
        />
      ),
    });
  };

  /**
   * Directly adds a cut to a ChoiceGroup option by ID — used by drag-and-drop
   * so no modal is needed (the user already chose by dragging).
   * @param questionId
   * @param optionId
   * @param cutId
   */
  const handleDropCutToOption = (
    questionId: string,
    optionId: string,
    cutId: string,
  ) => {
    const question = questionnaire.questions.find((q) => q.id === questionId);
    if (
      !question ||
      question.type !== EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup
    )
      return;

    const choiceQuestion = question as import('../types').ChoiceGroupQuestion;
    const option = choiceQuestion.options.find((opt) => opt.id === optionId);
    // Skip if cut is already in the option
    if (!option || option.cutIds.includes(cutId)) return;

    const cut = capabilities.cuts.find((c) => c.id === cutId);
    const updatedOptions = choiceQuestion.options.map((opt) =>
      opt.id === optionId ? { ...opt, cutIds: [...opt.cutIds, cutId] } : opt,
    );
    updateQuestion(questionId, { options: updatedOptions });
    push({
      title: 'Cut Added',
      body: `${cut?.title ?? 'Cut'} added to option`,
      bg: 'success',
    });
  };

  const handleRemoveCutFromOption = (
    questionId: string,
    optionId: string,
    cutId: string,
  ) => {
    const question = questionnaire.questions.find((q) => q.id === questionId);
    if (
      !question ||
      question.type !== EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup
    )
      return;

    const updatedOptions = (
      question as import('../types').ChoiceGroupQuestion
    ).options.map((opt) =>
      opt.id === optionId
        ? { ...opt, cutIds: opt.cutIds.filter((id: string) => id !== cutId) }
        : opt,
    );
    updateQuestion(questionId, { options: updatedOptions });
  };

  // ---------------------------------------------------------------------------
  // Add question handlers (one per question type)
  // ---------------------------------------------------------------------------

  const handleAddGlobalSpecQuestion = (insertAtIndex: number) => {
    // Any spec can be used as a standalone question — no more 'global'-type filter.
    if (capabilities.specifications.length === 0) {
      push({
        title: 'No Specifications',
        body: 'You need to add specifications in the catalog first.',
        bg: 'warning',
      });
      return;
    }

    save({
      type: 'save',
      title: 'Add Specification Question',
      initialValues: {
        title: '',
        description: '',
        imageUrl: '',
        specificationId: '',
        isRequired: true,
      },
      body: (
        <GlobalSpecQuestionForm
          specifications={capabilities.specifications}
          capabilityId={capabilityId}
        />
      ),
      onSubmit: async (values) => {
        addQuestionAtIndex(
          {
            type: EnumCutsheetQuestionnaireQuestionsType.GlobalSpec,
            ...values,
          },
          insertAtIndex,
        );
      },
    });
  };

  const handleAddFreeSelectionQuestion = (insertAtIndex: number) => {
    if (capabilities.cuts.length === 0) {
      push({
        title: 'No Cuts Available',
        body: 'You need to add cuts in the Capabilities section first.',
        bg: 'warning',
      });
      return;
    }

    save({
      type: 'save',
      title: 'Add Multi-Choice Cuts Question',
      initialValues: {
        title: '',
        description: '',
        imageUrl: '',
        availableCutIds: [],
        minSelections: undefined,
        maxSelections: undefined,
      },
      body: (
        <FreeSelectionQuestionForm
          cuts={capabilities.cuts}
          capabilityId={capabilityId}
        />
      ),
      onSubmit: async (values) => {
        addQuestionAtIndex(
          {
            type: EnumCutsheetQuestionnaireQuestionsType.FreeSelection,
            ...values,
          },
          insertAtIndex,
        );
      },
    });
  };

  const handleAddChoiceGroupQuestion = (insertAtIndex: number) => {
    if (capabilities.cuts.length === 0) {
      push({
        title: 'No Cuts Available',
        body: 'You need to add cuts in the Capabilities section first.',
        bg: 'warning',
      });
      return;
    }

    save({
      type: 'save',
      title: 'Add Either/Or Cuts Question',
      initialValues: {
        title: '',
        description: '',
        imageUrl: '',
        allowSubSelection: false,
        options: [],
        required: false,
      },
      body: (
        <ChoiceGroupQuestionForm
          cuts={capabilities.cuts}
          capabilityId={capabilityId}
        />
      ),
      onSubmit: async (values) => {
        addQuestionAtIndex(
          {
            type: EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup,
            ...values,
          },
          insertAtIndex,
        );
      },
    });
  };

  const handleAddTrimRankingQuestion = (insertAtIndex: number) => {
    if (capabilities.trim.length === 0) {
      push({
        title: 'No Trim Available',
        body: 'You need to add trim options in the Capabilities section first.',
        bg: 'warning',
      });
      return;
    }
    const hasPercentage = questionnaire.questions.some(
      (q) => q.type === EnumCutsheetQuestionnaireQuestionsType.TrimPercentage,
    );
    if (hasPercentage) {
      push({
        title: 'Trim question already exists',
        body: 'This form already has a Trim by Percentage question. Only one trim question type (by weight OR by percentage) can be used per form.',
        bg: 'warning',
      });
      return;
    }

    save({
      type: 'save',
      title: 'Add Trim by Weight Question',
      initialValues: {
        title: '',
        description: '',
        imageUrl: '',
        availableTrimIds: [],
        maxSelections: undefined,
        required: false,
      },
      body: (
        <TrimRankingQuestionForm
          trims={capabilities.trim}
          capabilityId={capabilityId}
        />
      ),
      onSubmit: async (values) => {
        addQuestionAtIndex(
          {
            type: EnumCutsheetQuestionnaireQuestionsType.TrimRanking,
            ...values,
          },
          insertAtIndex,
        );
      },
    });
  };

  const handleAddTrimPercentageQuestion = (insertAtIndex: number) => {
    if (capabilities.trim.length === 0) {
      push({
        title: 'No Trim Available',
        body: 'You need to add trim options in the Capabilities section first.',
        bg: 'warning',
      });
      return;
    }
    const hasRanking = questionnaire.questions.some(
      (q) => q.type === EnumCutsheetQuestionnaireQuestionsType.TrimRanking,
    );
    if (hasRanking) {
      push({
        title: 'Trim question already exists',
        body: 'This form already has a Trim by Weight question. Only one trim question type (by weight OR by percentage) can be used per form.',
        bg: 'warning',
      });
      return;
    }

    save({
      type: 'save',
      title: 'Add Trim by Percentage Question',
      initialValues: {
        title: '',
        description: '',
        imageUrl: '',
        availableTrimIds: [],
        requireExactTotal: true,
        required: false,
      },
      body: (
        <TrimPercentageQuestionForm
          trims={capabilities.trim}
          capabilityId={capabilityId}
        />
      ),
      onSubmit: async (values) => {
        addQuestionAtIndex(
          {
            type: EnumCutsheetQuestionnaireQuestionsType.TrimPercentage,
            ...values,
          },
          insertAtIndex,
        );
      },
    });
  };

  const handleAddExtrasSelectionQuestion = (insertAtIndex: number) => {
    if (capabilities.extras.length === 0) {
      push({
        title: 'No Extras Available',
        body: 'You need to add extras in the catalog first.',
        bg: 'warning',
      });
      return;
    }

    save({
      type: 'save',
      title: 'Add Extras Selection Question',
      initialValues: {
        title: '',
        description: '',
        imageUrl: '',
        availableExtraIds: [],
        minSelections: undefined,
        maxSelections: undefined,
        required: false,
      },
      body: (
        <ExtrasSelectionQuestionForm
          extras={capabilities.extras}
          capabilityId={capabilityId}
        />
      ),
      onSubmit: async (values) => {
        addQuestionAtIndex(
          {
            type: EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection,
            ...values,
          },
          insertAtIndex,
        );
        push({
          title: 'Success',
          body: 'Extras selection question added',
          bg: 'success',
        });
      },
    });
  };

  const handleAddInfoSlideQuestion = (insertAtIndex: number) => {
    save({
      type: 'save',
      title: 'Add Information Slide',
      initialValues: {
        title: '',
        description: '',
        details: '',
        imageUrl: '',
        required: false,
      },
      body: <InfoSlideQuestionForm capabilityId={capabilityId} />,
      onSubmit: async (values) => {
        addQuestionAtIndex(
          { type: EnumCutsheetQuestionnaireQuestionsType.InfoSlide, ...values },
          insertAtIndex,
        );
        push({
          title: 'Success',
          body: 'Information slide added',
          bg: 'success',
        });
      },
    });
  };

  /**
   * Routes a selected question type + insertion index to the correct add handler.
   * @param type
   * @param insertAtIndex
   */
  const handleAddQuestion = (
    type: AddableQuestionType,
    insertAtIndex: number,
  ) => {
    switch (type) {
      case 'global-spec':
        handleAddGlobalSpecQuestion(insertAtIndex);
        break;
      case EnumCutsheetQuestionnaireQuestionsType.FreeSelection:
        handleAddFreeSelectionQuestion(insertAtIndex);
        break;
      case EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup:
        handleAddChoiceGroupQuestion(insertAtIndex);
        break;
      case EnumCutsheetQuestionnaireQuestionsType.TrimRanking:
        handleAddTrimRankingQuestion(insertAtIndex);
        break;
      case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage:
        handleAddTrimPercentageQuestion(insertAtIndex);
        break;
      case EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection:
        handleAddExtrasSelectionQuestion(insertAtIndex);
        break;
      case EnumCutsheetQuestionnaireQuestionsType.InfoSlide:
        handleAddInfoSlideQuestion(insertAtIndex);
        break;
      default:
        break;
    }
  };

  // ---------------------------------------------------------------------------
  // Edit question handler (dispatches per type)
  // ---------------------------------------------------------------------------

  const handleEditQuestion = (question: Question) => {
    switch (question.type) {
      case EnumCutsheetQuestionnaireQuestionsType.GlobalSpec:
        save({
          type: 'save',
          title: 'Edit Specification Question',
          initialValues: question,
          body: (
            <GlobalSpecQuestionForm
              specifications={capabilities.specifications}
              capabilityId={capabilityId}
            />
          ),
          onSubmit: async (values) => {
            updateQuestion(question.id, values);
            push({
              title: 'Success',
              body: 'Global specification question updated',
              bg: 'success',
            });
          },
        });
        break;

      case EnumCutsheetQuestionnaireQuestionsType.FreeSelection:
        save({
          type: 'save',
          title: 'Edit Multi-Choice Cuts Question',
          initialValues: question,
          body: (
            <FreeSelectionQuestionForm
              cuts={capabilities.cuts}
              capabilityId={capabilityId}
            />
          ),
          onSubmit: async (values) => {
            updateQuestion(question.id, values);
            push({
              title: 'Success',
              body: 'Multi-choice cuts question updated',
              bg: 'success',
            });
          },
        });
        break;

      case EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup:
        save({
          type: 'save',
          title: 'Edit Either/Or Cuts Question',
          initialValues: question,
          hideHeader: true,
          body: (
            <ChoiceGroupQuestionForm
              cuts={capabilities.cuts}
              capabilityId={capabilityId}
            />
          ),
          onSubmit: async (values) => {
            updateQuestion(question.id, values);
            push({
              title: 'Success',
              body: 'Either/or cuts question updated',
              bg: 'success',
            });
          },
        });
        break;

      case EnumCutsheetQuestionnaireQuestionsType.TrimRanking:
        save({
          type: 'save',
          title: 'Edit Trim by Weight Question',
          initialValues: question,
          body: (
            <TrimRankingQuestionForm
              trims={capabilities.trim}
              capabilityId={capabilityId}
            />
          ),
          onSubmit: async (values) => {
            updateQuestion(question.id, values);
            push({
              title: 'Success',
              body: 'Trim by weight question updated',
              bg: 'success',
            });
          },
        });
        break;

      case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage:
        save({
          type: 'save',
          title: 'Edit Trim by Percentage Question',
          initialValues: question,
          body: (
            <TrimPercentageQuestionForm
              trims={capabilities.trim}
              capabilityId={capabilityId}
            />
          ),
          onSubmit: async (values) => {
            updateQuestion(question.id, values);
            push({
              title: 'Success',
              body: 'Trim by percentage question updated',
              bg: 'success',
            });
          },
        });
        break;

      case EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection:
        save({
          type: 'save',
          title: 'Edit Extras Selection Question',
          initialValues: question,
          body: (
            <ExtrasSelectionQuestionForm
              extras={capabilities.extras}
              capabilityId={capabilityId}
            />
          ),
          onSubmit: async (values) => {
            updateQuestion(question.id, values);
            push({
              title: 'Success',
              body: 'Extras selection question updated',
              bg: 'success',
            });
          },
        });
        break;

      case EnumCutsheetQuestionnaireQuestionsType.InfoSlide:
        save({
          type: 'save',
          title: 'Edit Information Slide',
          initialValues: question,
          body: <InfoSlideQuestionForm capabilityId={capabilityId} />,
          onSubmit: async (values) => {
            updateQuestion(question.id, values);
            push({
              title: 'Success',
              body: 'Information slide updated',
              bg: 'success',
            });
          },
        });
        break;

      default:
        push({
          title: 'Unknown Question Type',
          body: 'Cannot edit this type of question',
          bg: 'error',
        });
        break;
    }
  };

  const handleDeleteQuestion = (questionId: string) => {
    const question = questionnaire.questions.find((q) => q.id === questionId);
    if (!question) return;

    ask({
      type: 'ask',
      title: 'Delete Question',
      body: `Are you sure you want to delete "${question.title}"? This action cannot be undone.`,
      onConfirm: () => {
        removeQuestion(questionId);
        push({ title: 'Success', body: 'Question deleted', bg: 'success' });
      },
    });
  };

  /** Opens the questionnaire settings modal with General / Pricing Overrides / Default Selections tabs. */
  const handleEditQuestionnaireInfo = () => {
    save({
      type: 'save',
      title: 'Questionnaire Settings',
      hideHeader: true,
      size: 'xl',
      initialValues: {
        name: questionnaire.name,
        description: questionnaire.description || '',
        status: questionnaire.status ?? 'draft',
        // Seed the new tabs with any already-saved values
        pricingOverrides: questionnaire.pricingOverrides ?? {},
        specOptionPricingOverrides:
          questionnaire.specOptionPricingOverrides ?? {},
        defaultSelections: questionnaire.defaultSelections,
        useCustomPrintSettings: questionnaire.useCustomPrintSettings ?? false,
        printSettings: questionnaire.printSettings ?? {},
      },
      body: (
        <QuestionnaireSettingsForm
          capabilities={capabilities}
          questionnaire={questionnaire}
          capability={capability}
        />
      ),
      footer: ({ formikProps, modalProps }) => (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%' }}
        >
          {onArchiveForm ? (
            <MuiButton
              color="warning"
              variant="text"
              startIcon={<ArchiveIcon />}
              onClick={() => {
                ask({
                  type: 'ask',
                  title: 'Archive Form?',
                  body: `Archive "${questionnaire.name}"? It will be hidden from the form picker by default — toggle "Include archived" to restore it.`,
                  onConfirm: onArchiveForm,
                });
              }}
            >
              Archive Form
            </MuiButton>
          ) : (
            <Box />
          )}
          <Stack direction="row" spacing={1}>
            <MuiButton variant="outlined" onClick={modalProps.onClose}>
              Cancel
            </MuiButton>
            <MuiButton
              variant="contained"
              onClick={() => formikProps?.submitForm()}
            >
              Save
            </MuiButton>
          </Stack>
        </Stack>
      ),
      onSubmit: async (values) => {
        await saveQuestionnaire(values);
        push({
          title: 'Success',
          body: 'Questionnaire settings saved',
          bg: 'success',
        });
      },
    });
  };

  return {
    handleAddQuestion,
    handleEditQuestion,
    handleDeleteQuestion,
    handleEditQuestionnaireInfo,
    handleAddItemToQuestion,
    handleAddItemsToQuestion,
    handleRemoveItemFromQuestion,
    handleReorderItemsInQuestion,
    handleAddCutToOption,
    handleDropCutToOption,
    handleRemoveCutFromOption,
  };
};
